import { COMPANY_SEARCH_API_CHANGES } from '@aibees/elephant/constants';
import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { HttpMethod } from '@aibees/shared/definition';
import { useDebouncedMutation } from '@aibees/shared/hooks';
import {
  baseQueryClient,
  useBaseMutation,
  useBaseQuery,
} from '@aibees/shared/http';
import { toNumber } from '@aibees/shared/utils';
import { useWorkspace } from '@aibees/shared/workspace';
import { useCallback, useEffect, useRef } from 'react';
import { useCompanySearchItemsStore } from '../store';
import {
  CompanyRequestType,
  CompanySearchBody,
  CompanySearchResponse,
} from './types';

export function useBrowseUpdatedCompanies() {
  const { params } = useSearchQueryFilter();
  const workspace = useWorkspace()?.id;
  const { companySearchItems } = useCompanySearchItemsStore();
  const tableIds = companySearchItems.map((i) => i.id);

  // Create a ref to always store the latest tableIds
  const tableIdsRef = useRef(tableIds);
  useEffect(() => {
    tableIdsRef.current = tableIds;
  }, [tableIds]);

  // Ensure there is a query with key 'useBrowseUpdatedCompanies.name'
  // we don't use this query directly and just using it for a cached store for updated data after mutation
  useBaseQuery(
    [useBrowseUpdatedCompanies.name],
    {},
    {
      enabled: false,
    }
  );

  const mutation = useBaseMutation<
    CompanySearchBody & { page: number; size: number },
    CompanySearchResponse
  >(
    {
      url: COMPANY_SEARCH_API_CHANGES.replace(':wid', String(workspace)),
      method: HttpMethod.post,
      headers: { 'x-customer-id': workspace },
    },
    {
      onSuccess: (data) => {
        // Store result in cache
        baseQueryClient.setQueryData<CompanySearchResponse>(
          [useBrowseUpdatedCompanies.name],
          data
        );
      },
    }
  );

  const debouncedMutation = useDebouncedMutation(
    mutation,
    useBrowseUpdatedCompanies.name,
    1000
  );

  return {
    ...mutation,
    mutate: useCallback(() => {
      const payload = {
        page: 0,
        size: toNumber(params?.size) ?? 25,
        account: {
          id: {
            any: { include: tableIdsRef.current },
          },
        },
        contact: {},
        type: CompanyRequestType.SIMILARITY,
        value: params?.value?.split(FILTER_VALUE_SEPARATOR),
      };

      return debouncedMutation.debouncedMutate(payload);
    }, [debouncedMutation, params, tableIdsRef]),
  };
}
